import React, { useState, useEffect, useRef } from 'react';
import RefineReportButton from './RefineReportButton';

interface ProblemReportOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  screenshot: string;
  onSubmit: (description: string, screenshot: string, url: string, selectedArea: string, cc_me: boolean) => void;
}

const ProblemReportOverlay: React.FC<ProblemReportOverlayProps> = ({ isOpen, onClose, screenshot, onSubmit }) => {
  const [description, setDescription] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [isSelectingArea, setIsSelectingArea] = useState(true);
  const [cc_me, setCCMe] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleAreaClick = (event: React.MouseEvent) => {
    if (isSelectingArea) {
      const target = event.target as HTMLElement;
      setSelectedArea(target.id || target.className || 'Unknown area');
      setIsSelectingArea(false);
    }
  };

  const handleSubmit = async () => {
    if (overlayRef.current) {
      onSubmit(description, screenshot, window.location.href, selectedArea, cc_me);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={overlayRef}
      className="fixed inset-0 bg-black/50 dark:bg-black dark:bg-opacity-70 flex justify-center items-start z-50 overflow-y-auto"
      onClick={handleAreaClick}
    >
      <div className="p-6 rounded-lg max-w-md w-full mx-4 my-8 bg-white dark:bg-gray-800 border border-border shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Send feedback</h2>
        {isSelectingArea ? (
          <p className="text-gray-700 dark:text-gray-300">Click on the area where you're experiencing the problem.</p>
        ) : (
          <>
            <p className="mb-2 text-gray-700 dark:text-gray-300">Selected area: {selectedArea}</p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the problem..."
              className="w-full h-32 p-2 border border-gray-300 dark:border-gray-600 rounded mb-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            />
            <label className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                checked={cc_me}
                onChange={(e) => setCCMe(e.target.checked)}
                className="h-4 w-4 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              <span className="text-gray-900 dark:text-gray-100">Send me a copy of this report</span>
            </label>
            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-700 rounded hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors duration-200"
              >
                Submit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProblemReportOverlay;